/*
 * Counts down to the next midnight in a given IANA time zone.
 *
 * The zone is read from the element's data-time-zone attribute so the promo
 * copy stays owned by the theme editor and never lives in JavaScript.
 * All arithmetic is done on real instants derived from the zone's actual
 * offset at that moment, so CST/CDT transitions are handled automatically.
 */
(() => {
  const formatters = {};

  function getFormatter(timeZone) {
    if (formatters[timeZone] === undefined) {
      try {
        formatters[timeZone] = new Intl.DateTimeFormat('en-US', {
          timeZone: timeZone,
          hour12: false,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      } catch (e) {
        formatters[timeZone] = null;
      }
    }

    return formatters[timeZone];
  }

  // Wall-clock parts of an instant, as seen in the given zone.
  function zonedParts(formatter, instant) {
    const parts = {};
    formatter.formatToParts(instant).forEach(({ type, value }) => {
      if (type !== 'literal') parts[type] = Number(value);
    });

    // Some engines format midnight as hour 24 when hour12 is false.
    parts.hour = parts.hour % 24;

    return parts;
  }

  // Offset in ms between the zone's wall clock and UTC at this instant.
  function zoneOffset(formatter, instant) {
    const parts = zonedParts(formatter, instant);
    const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);

    return asUtc - Math.floor(instant / 1000) * 1000;
  }

  // Real instant of the upcoming midnight in the zone.
  function nextMidnight(formatter, instant) {
    const parts = zonedParts(formatter, instant);
    // Tomorrow at 00:00:00 wall clock, read as if it were UTC.
    // Date.UTC rolls day/month/year over for us.
    const wallClock = Date.UTC(parts.year, parts.month - 1, parts.day + 1, 0, 0, 0);

    // Convert the wall clock back to an instant, then refine once in case the
    // offset changes between now and midnight (daylight saving transition).
    const estimate = wallClock - zoneOffset(formatter, instant);

    return wallClock - zoneOffset(formatter, estimate);
  }

  function formatRemaining(milliseconds) {
    const total = Math.max(0, Math.floor(milliseconds / 1000));

    return [Math.floor(total / 3600), Math.floor((total % 3600) / 60), total % 60]
      .map((value) => String(value).padStart(2, '0'))
      .join(':');
  }

  if (!customElements.get('announcement-countdown')) {
    customElements.define(
      'announcement-countdown',
      class AnnouncementCountdown extends HTMLElement {
        timeoutId = undefined;

        connectedCallback() {
          this.output = this.querySelector('[data-countdown-output]');
          this.formatter = getFormatter(this.dataset.timeZone);
          if (!this.output || !this.formatter) return;

          this.render();
          this.scheduleTick();
        }

        disconnectedCallback() {
          clearTimeout(this.timeoutId);
        }

        // Re-align to the system clock each tick so the display never drifts
        // and recovers on its own after the tab is backgrounded or suspended.
        scheduleTick() {
          this.timeoutId = setTimeout(() => {
            this.render();
            this.scheduleTick();
          }, 1000 - (Date.now() % 1000));
        }

        render() {
          const now = Date.now();
          this.output.textContent = formatRemaining(nextMidnight(this.formatter, now) - now);
        }
      }
    );
  }
})();
